import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { inRange, matchesCase, newestFirst } from "@/lib/caseList";
import { AddedFilter } from "@/components/portal/crm/AddedFilter";
import { gbp, caseState, DOT } from "@/lib/stages";
import { ukDay, ukDayTime, ukDayYear } from "@/lib/time";
import { toast } from "sonner";
import {
  Btn, CellMain, CellSub, Divider, Empty, Kv, LinkRow, Lname, Money, PageHead, Panel, SearchBox, Sec, StatusChip, StatStrip, Tb, Td, Th,
} from "@/components/portal/crm/kit";
import { NeedsAWord } from "@/components/hub/NeedsAWord";
import { FastTrackTag } from "@/components/portal/crm/FastTrackTag";
import { CaseStamp } from "@/components/portal/crm/CaseStamp";
import { RouteCaseDialog } from "@/components/hub/RouteCaseDialog";

/* The desk — mockup 08, wired to the live book. Every figure here is computed from
   `/hub/pipeline/value` (the same ledger the case page prints) and `/hub/leads`. */

const FILTERS = [
  { key: "all", label: "All" },
  { key: "new", label: "Submitted", tone: "ink" },
  { key: "sent", label: "With lenders", tone: "ink" },
  { key: "in_review", label: "Under assessment", tone: "blue" },
  { key: "awaiting_docs", label: "Awaiting documents", tone: "amber" },
  { key: "preparing_offer", label: "Preparing offer", tone: "teal" },
  { key: "offer_made", label: "Offer issued", tone: "mint" },
  { key: "completed", label: "Funded", tone: "green" },
  { key: "declined", label: "Declined", tone: "red" },
];

const deskKey = (d) => d.phase || d.stage;

const Act = ({ n, label, tone, to, testId }) => (
  <Link className={`act${tone ? ` ${tone}` : ""}`} to={to} data-testid={testId}>
    <div className="edge" />
    <div>
      <div className="n">{n}</div>
      <div className="t">{label}</div>
    </div>
  </Link>
);

export default function Desk() {
  const nav = useNavigate();
  const [book, setBook] = useState(null);
  const [leads, setLeads] = useState([]);
  const [summary, setSummary] = useState(null);
  const [stalled, setStalled] = useState([]);
  const [waiting, setWaiting] = useState([]);
  const [apps, setApps] = useState([]);
  const [desks, setDesks] = useState([]);
  const [pendingPartners, setPendingPartners] = useState([]);
  const [quietSeats, setQuietSeats] = useState([]);
  const [q, setQ] = useState("");
  const [added, setAdded] = useState({ key: "any" });
  const [filter, setFilter] = useState("all");
  const [sweeping, setSweeping] = useState(false);
  const [routing, setRouting] = useState(false);

  const load = useCallback(async () => {
    const get = (p, fallback) => api.get(p).then((r) => r.data).catch(() => fallback);
    const [v, L, s, st, w, a, d, pp, qs] = await Promise.all([
      get("/hub/pipeline/value", null),
      get("/hub/leads?limit=300", { items: [] }),
      get("/hub/summary", null),
      get("/hub/stalled-deals", { items: [] }),
      get("/hub/chat/unanswered", { items: [] }),
      get("/hub/applications?status=pending", { items: [] }),
      get("/hub/lenders", { items: [] }),
      get("/hub/partners/pending", { items: [] }),
      get("/hub/seats/quiet", { items: [] }),
    ]);
    setBook(v); setLeads(L.items || []); setSummary(s);
    setStalled(st.items || []); setWaiting(w.items || []); setApps(a.items || []);
    setDesks(d.items || []); setPendingPartners(pp.items || []); setQuietSeats(qs.items || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const routesById = useMemo(() => {
    const m = {};
    (leads || []).forEach((l) => { m[l.id] = l; });
    return m;
  }, [leads]);

  const deals = book?.deals || [];
  const open = deals.filter((d) => !["completed", "declined"].includes(d.stage));
  const offers = deals.filter((d) => d.stage === "offer_made");
  const withDesk = deals.filter((d) => d.stage === "sent");

  const expiries = useMemo(() => {
    const out = [];
    (leads || []).forEach((l) => (l.routes || []).forEach((r) => {
      const on = r.offer?.expires_on;
      if (on && r.status === "offer_made") out.push(on);
    }));
    return out.sort();
  }, [leads]);
  const soon = expiries.filter((e) => {
    const days = (new Date(e) - new Date()) / 86400000;
    return days >= -1 && days <= 7;
  });

  const windowed = useMemo(() => newestFirst(deals).filter((d) => inRange(d.created_at, added)), [deals, added]);
  const counts = useMemo(() => {
    const c = { all: windowed.length };
    FILTERS.slice(1).forEach((f) => { c[f.key] = windowed.filter((d) => deskKey(d) === f.key).length; });
    return c;
  }, [windowed]);

  const rows = useMemo(() => windowed
    .filter((d) => (filter === "all" ? true : deskKey(d) === filter))
    .filter((d) => {
      const lead = routesById[d.id] || {};
      const panel = (lead.routes || []).map((r) => r.company_name || "");
      return matchesCase({ ...d, applicant_email: lead.applicant_email, applicant_phone: lead.applicant_phone,
                           company_number: lead.company_number }, q, [d.source?.name, ...panel]);
    })
    .slice(0, 60), [windowed, filter, q, routesById]);

  const deskBook = useMemo(() => {
    const rowsByDesk = {};
    (desks || []).forEach((L) => {
      rowsByDesk[L.id] = {
        id: L.id, name: L.company_name, kind: L.company_type || "Panel desk",
        terms: L.commission_type === "income_share" ? `${L.commission_pct}%`
          : L.commission_type === "flat" ? gbp(L.commission_flat_gbp) : `${L.commission_pct}%`,
        basis: L.commission_type === "income_share" ? "Of their income"
          : L.commission_type === "flat" ? "Flat per deal" : "Of funded",
        turnaround: L.turnaround_days, live: 0, month: 0,
      };
    });
    const monthStart = new Date();
    monthStart.setUTCDate(1); monthStart.setUTCHours(0, 0, 0, 0);
    (leads || []).forEach((l) => (l.routes || []).forEach((r) => {
      const row = rowsByDesk[r.lender_id];
      if (!row) return;
      if (!["declined", "completed"].includes(r.status)) row.live += 1;
      if (r.status === "completed") {
        const d = deals.find((x) => x.id === l.id);
        if (d && new Date(d.updated_at) >= monthStart) row.month += d.value?.gross_gbp || 0;
      }
    }));
    return Object.values(rowsByDesk).sort((a, b) => b.month - a.month || b.live - a.live).slice(0, 6);
  }, [desks, leads, deals]);

  const sweep = async () => {
    setSweeping(true);
    try {
      const r = await api.post("/hub/lead-nudges/run");
      toast.success(r.data?.sent ? `Chased ${r.data.sent} desk${r.data.sent === 1 ? "" : "s"}` : "Nothing needed chasing");
      load();
    } catch { toast.error("The sweep didn't run — try again in a moment"); }
    finally { setSweeping(false); }
  };

  if (!book) return <Empty testId="hub-desk-loading">Reading the book…</Empty>;

  const t = book.totals || {};
  const conv = summary?.leads?.conversion_pct ?? 0;

  return (
    <>
      <PageHead
        eyebrow={ukDayYear(new Date().toISOString())}
        title="The desk"
        lede="Every case, both halves of the economics, and whatever is waiting on somebody. Partners see their own slice of this. You see all of it."
        testId="hub-desk-head"
        actions={
          <>
            <Btn onClick={sweep} disabled={sweeping} testId="hub-desk-sweep">
              {sweeping ? "Chasing…" : "Run the chase sweep"}
            </Btn>
            <Btn kind="primary" onClick={() => setRouting(true)} testId="hub-desk-route">Route a case</Btn>
          </>
        }
      />

      <StatStrip
        testId="hub-desk-stats"
        items={[
          { k: "Live cases", v: String(open.length), h: `${gbp(t.open?.funding_gbp, { compact: true })} of facilities in play`, testId: "hub-desk-live" },
          { k: "Waiting on a desk", v: String(withDesk.length), h: stalled.length ? `${stalled.length} past SLA — chase them` : "None past SLA", testId: "hub-desk-waiting" },
          { k: "Offers out", v: String(offers.length), h: expiries.length ? `Earliest expiry ${ukDay(expiries[0])}` : "No expiry dates on file", testId: "hub-desk-offers" },
          { k: "Fee book, this month", v: gbp(t.won_month?.net_gbp), h: `${gbp(t.open?.weighted_net_gbp)} weighted in the pipeline`, testId: "hub-desk-feebook" },
        ]}
      />

      <Sec title="Needs the desk" right={<Btn sm onClick={() => nav("/hub/desk-tools")} testId="hub-desk-clear">Open desk tools</Btn>}>
        <div className="strip" data-testid="hub-desk-strip">
          <Act n={stalled.length} label="Cases past SLA" tone={stalled.length ? "red" : undefined} to="/hub/desk-tools" testId="hub-act-sla" />
          <Act n={soon.length} label="Offers expiring this week" tone={soon.length ? "amber" : undefined} to="/hub/pipeline" testId="hub-act-expiring" />
          <Act n={waiting.length} label="Partner messages unanswered" tone={waiting.length ? "blue" : undefined} to="/hub/messages" testId="hub-act-messages" />
          <Act n={apps.length} label="Partner applications to approve" to="/hub/applications" testId="hub-act-applications" />
          <Act n={pendingPartners.length} label="Sign-ups awaiting approval" tone={pendingPartners.length ? "amber" : undefined} to="/hub/referrers?pending=1" testId="hub-act-pending-partners" />
          <Act n={quietSeats.length} label="Seats gone quiet (14 days)" tone={quietSeats.length ? "blue" : undefined} to="/hub/desk-tools" testId="hub-act-quiet-seats" />
        </div>
      </Sec>

      <Sec>
        <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap", marginBottom: 16 }}>
          <SearchBox value={q} onChange={setQ} placeholder="Search name, business, email, phone, introducer or desk" testId="hub-desk-search" />
          <AddedFilter value={added} onChange={setAdded} testId="hub-desk-added" />
          <div className="chips" style={{ marginLeft: "auto" }}>
            {FILTERS.map((f) => (
              <button key={f.key} className={`chip${filter === f.key ? " on" : ""}`} onClick={() => setFilter(f.key)}
                      data-testid={`hub-desk-filter-${f.key}`}>
                {f.tone && <i className="sq" style={{ background: DOT[f.tone] }} />}
                {f.label}<span className="n">{counts[f.key] ?? 0}</span>
              </button>
            ))}
          </div>
        </div>

        <Panel pad={false} testId="hub-desk-cases" style={{ overflow: "hidden" }}
               footer={
                 <>
                   <span>Estimates come from the terms snapshotted onto each route when the case was sent. They firm up when a desk confirms what it earned.</span>
                   <span style={{ fontWeight: 700, color: "var(--ink-3)" }}>{ukDayTime(book.as_of)} UK</span>
                 </>
               }>
          {rows.length === 0 ? (
            <Empty testId="hub-desk-cases-empty">Nothing matches that. Clear the search or pick another stage.</Empty>
          ) : (
            <Tb testId="hub-desk-cases-table"
                head={<>
                  <Th style={{ width: "26%" }}>Case</Th>
                  <Th style={{ width: "17%" }}>Introducer</Th>
                  <Th style={{ width: "24%" }}>Panel</Th>
                  <Th style={{ width: "11%" }}>Stage</Th>
                  <Th className="r" style={{ width: "11%" }}>Our fee</Th>
                  <Th className="r" style={{ width: "11%" }}>Their commission</Th>
                </>}>
              {rows.map((d) => {
                const lead = routesById[d.id] || {};
                const routes = lead.routes || [];
                const live = routes.filter((r) => r.status !== "declined");
                const passed = routes.length - live.length;
                const state = caseState({ ...d, routes, idle_days: d.idle_days });
                const est = !!d.value?.gross_estimated;
                const quiet = (d.flags || []).includes("stalled");
                return (
                  <tr key={d.id} className={(d.flags || []).includes("needs_rehome") ? "flag" : quiet ? "flag-amber" : undefined}
                      onClick={() => nav(`/hub/leads/${d.id}`)} style={{ cursor: "pointer" }}
                      data-testid={`hub-desk-case-${d.id}`}>
                    <Td className="lead" style={{ padding: "22px 20px" }}>
                      <CellMain style={{ fontSize: 15 }}>{d.business_name || d.applicant_name}</CellMain>
                      <CellSub>{gbp(d.amount_gbp)} · {d.finance_type} · {d.reference || "no ref"}</CellSub>
                      <div><CaseStamp at={d.created_at} testId={`hub-desk-stamp-${d.id}`} /></div>
                      {d.fast_track && <div style={{ marginTop: 8 }}><FastTrackTag lead={d} testId={`hub-desk-fast-track-${d.id}`} /></div>}
                    </Td>
                    <Td>
                      <CellMain style={{ fontSize: 13.5 }}>{d.source?.name}</CellMain>
                      <CellSub>{d.source?.terms || (d.source?.direct ? "No introducer" : "—")}</CellSub>
                    </Td>
                    <Td>
                      {routes.length === 0
                        ? <CellSub style={{ margin: 0 }}>Not routed yet</CellSub>
                        : (
                          <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
                            {live.slice(0, 3).map((r) => (
                              <Lname key={r.lender_id} tone={quiet ? "quiet" : undefined}>
                                {r.company_name}{quiet && d.idle_days ? ` — silent ${d.idle_days}d` : ""}
                              </Lname>
                            ))}
                            {passed > 0 && <Lname tone="muted">{passed} passed</Lname>}
                          </div>
                        )}
                    </Td>
                    <Td><StatusChip state={state} testId={`hub-desk-stage-${d.id}`} /></Td>
                    <Td className="r">
                      {d.value?.priced
                        ? <Money v={d.value.gross_gbp}
                                 tone={d.stage === "completed" ? "green" : d.stage === "declined" || est ? "muted" : "ink"}
                                 sub={d.stage === "declined" ? "Case closed" : est ? "Estimated" : "Confirmed"} />
                        : <Money raw="—" tone="muted" />}
                    </Td>
                    <Td className="r">
                      {d.value?.introducer_gbp
                        ? <Money v={d.value.introducer_gbp} tone={d.stage === "completed" ? "green" : "muted"}
                                 sub={d.stage === "completed" ? "On the next run"
                                   : d.stage === "declined" ? (d.value.committed_cost ? "Owed anyway" : "Not payable")
                                     : "If it completes"} />
                        : <Money raw="—" tone="muted" />}
                    </Td>
                  </tr>
                );
              })}
            </Tb>
          )}
        </Panel>
      </Sec>

      <Sec>
        <div className="two">
          <div>
            <div className="sec-head">
              <div>
                <h2 className="s">Where the fee book comes from</h2>
                <p className="sec-sub">Hub only. No partner sees this screen, and no desk sees another desk&apos;s terms.</p>
              </div>
              <Btn sm onClick={() => nav("/hub/lenders")} testId="hub-desk-panel-terms">Panel terms</Btn>
            </div>
            <Panel pad={false} testId="hub-desk-feebook-table" style={{ overflow: "hidden" }}
                   footer={<span>A case sent to two desks on different terms carries the whole spread in our fee — which is exactly the range the introducer sees, without the rates behind it.</span>}>
              {deskBook.length === 0 ? (
                <Empty>No desks on the panel yet.</Empty>
              ) : (
                <Tb head={<><Th>Desk</Th><Th>Our terms</Th><Th>Live</Th><Th>Decision speed</Th><Th className="r">This month</Th></>}>
                  {deskBook.map((r) => (
                    <tr key={r.id} data-testid={`hub-desk-feebook-${r.id}`}>
                      <Td className="lead"><CellMain>{r.name}</CellMain><CellSub>{r.kind}</CellSub></Td>
                      <Td><Money raw={r.terms} /><CellSub>{r.basis}</CellSub></Td>
                      <Td><Money raw={String(r.live)} tone="muted" /></Td>
                      <Td><Money raw={r.turnaround ? `${r.turnaround} days` : "—"} tone={r.turnaround ? "ink" : "muted"} /></Td>
                      <Td className="r"><Money v={r.month} tone={r.month ? "ink" : "muted"} /></Td>
                    </tr>
                  ))}
                </Tb>
              )}
            </Panel>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            <NeedsAWord introducers={summary?.referrers?.active ?? 0}
                        desks={summary?.lenders?.active ?? 0} />

            <Panel title="The book so far" testId="hub-desk-month">
              <Kv rows={[
                ["Cases introduced", String(summary?.leads?.total ?? 0)],
                ["Funded", String(summary?.leads?.completed ?? 0)],
                ["Conversion", `${conv}%`],
                ["Net this month", gbp(t.won_month?.net_gbp)],
                ["Owed to partners", gbp(t.won?.deductions_gbp)],
              ]} />
              <Divider style={{ margin: "20px 0" }} />
              <LinkRow label="Open the pipeline ledger" onClick={() => nav("/hub/pipeline")} testId="hub-desk-link-pipeline" />
              <LinkRow label="Desk tools and health" onClick={() => nav("/hub/desk-tools")} testId="hub-desk-link-tools" />
            </Panel>
          </div>
        </div>
      </Sec>

      <RouteCaseDialog open={routing} onOpenChange={setRouting} onRouted={load} />
    </>
  );
}
